import React from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { useMediaQuery } from 'react-responsive';
import { actionRequiredService, GET_REVIEW_SIGN_DETAILS } from '@/api';
import {
	HttpResponse,
	ItemDocumentPackage,
	SignDocumentDetails,
} from '@/interfaces';
import { LoaderScreen, Typography } from '@/components/common';
import PdfViewer from '@/components/common/pdf-viewer/PdfViewer';
import { ClientSignDocumentLayout } from '../modal-review-sign/ClientSignDocumentView';
import { ClientSignMobileView } from '../modal-review-sign/ClientSignMobileView';
import { useClientSignSession } from '../modal-review-sign/hooks/useClientSignSession';
import { FormGeneralDocument } from './FormGeneralDocument';

interface Props {
	hasNextDocument: boolean;
	document: ItemDocumentPackage;
	onContinue: (isApproved: boolean) => void;
	isClientAction?: boolean;
}

export const GeneralDocument: React.FC<Props> = ({
	document,
	hasNextDocument,
	onContinue,
	isClientAction,
}) => {
	const { data: actionDetails, isLoading } = useQuery({
		queryKey: [GET_REVIEW_SIGN_DETAILS, document.id],
		queryFn: () =>
			actionRequiredService.getActionRequireReviewSign(String(document.id)),
	});

	if (isLoading || !actionDetails) {
		return <LoaderScreen />;
	}

	if (actionDetails.data.extracted) {
		return (
			<ClaimUSignGeneralDocument
				key={document.id}
				document={document}
				actionDetails={actionDetails}
				onContinue={onContinue}
			/>
		);
	}

	const isSignRequired = actionDetails.data.is_signature_required;
	const documentUrl = actionDetails.data.url_doc ?? '';
	const fields = actionDetails.data.required_fields ?? [];

	return (
		<section className="flex flex-col md:flex-row gap-6 h-auto md:h-full min-h-0">
			<div className="flex-1 min-w-0 md:h-full">
				{documentUrl && (
					<PdfViewer
						showZoom={true}
						showControls={false}
						pdfUrl={documentUrl}
						heightClassName="h-[495px] md:h-full"
						enableDragToPan
					/>
				)}
			</div>
			<div
				className={clsx('shrink-0 space-y-4 h-full min-h-0 flex flex-col', {
					'xl:w-[35%]': isClientAction,
					'xl:w-[23%]': !isClientAction,
				})}
			>
				<FormGeneralDocument
					hasNextDocument={hasNextDocument}
					onContinue={onContinue}
					actionRequiredId={document.id}
					applicationId={document.applicationId}
					isSignRequired={!!isSignRequired}
					actionDetails={actionDetails}
					fields={fields}
					isCompleted={document.isCompleted}
				/>
			</div>
		</section>
	);
};

const ClaimUSignGeneralDocument: React.FC<{
	document: ItemDocumentPackage;
	actionDetails: HttpResponse<SignDocumentDetails>;
	onContinue: (isApproved: boolean) => void;
}> = ({ document, actionDetails, onContinue }) => {
	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});
	const details = actionDetails.data;
	const documentUrl = details.url_doc ?? '';
	const extractedFields = details.extracted?.form?.fields;

	const session = useClientSignSession({
		applicationId: document.applicationId,
		actionRequiredId: document.id,
		extractedFields,
		requiredFields: details.required_fields,
		onClose: () => onContinue(false),
		onSubmitted: () => onContinue(true),
	});

	if (!documentUrl) {
		return (
			<div className="flex h-full items-center justify-center">
				<Typography type="regular-14" className="text-main-red">
					The document could not be loaded. Please try again.
				</Typography>
			</div>
		);
	}

	if (!isDesktopOrLaptop) {
		return (
			<div className="flex h-[calc(90vh-180px)] min-h-0 flex-col md:h-full">
				<ClientSignMobileView
					documentUrl={documentUrl}
					extractedFields={extractedFields}
					textPages={details.extracted?.text?.pages}
					documentImages={details.extracted?.vectorImages?.images}
					onClose={() => onContinue(false)}
					session={session}
					documentName={details.name_doc}
				/>
			</div>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-1 flex-col">
			<ClientSignDocumentLayout
				documentUrl={documentUrl}
				applicationId={document.applicationId}
				actionRequiredId={document.id}
				extractedFields={extractedFields}
				requiredFields={details.required_fields}
				actionDetails={details}
				onClose={() => onContinue(false)}
				onSubmitted={() => onContinue(true)}
				session={session}
			/>
		</div>
	);
};
